import { html, PropertyValueMap } from "lit";
import { property, queryAssignedElements } from "lit/decorators.js";
import { ref } from "lit/directives/ref.js";
import { DropdownListElement } from "../../base/dropdown-list-element";
import { watch } from "../../utils/watch";
import dropdownMenuStyle from "./dropdown-menu.css";
import dropdownStyle from "./dropdown.css";

export type DropDirection = "left" | "right" | "up" | "down";

/**
 * @summary `SgdsDropdown` toggles contextual overlays for displaying lists of links.
 * @slot default - slot for sgds-dropdown-item passed into dropdown's menu
 * @slot toggler - slot for the toggler that triggers the open and closing of menu, typically a button. Only pass in a single element into this slot
 *
 */
export class SgdsDropdown extends DropdownListElement {
  static styles = [...DropdownListElement.styles, dropdownStyle, dropdownMenuStyle];

  constructor() {
    super();
    this.menuRef = ref();
  }

  /** Controls auto-flipping of menu */
  @property({ type: Boolean, reflect: true, state: false })
  noFlip = false;

  /** When true, aligns right edge of menu with right edge of button */
  @property({ type: Boolean, reflect: true, state: false })
  menuAlignRight = false;

  /** The drop position of menu relative to the toggle button */
  @property({ type: String, reflect: true, state: false })
  drop: DropDirection = "down";

  /** Controls the close behaviour of dropdown menu. By default menu auto-closes when SgdsDropdownItem or area outside dropdown is clicked */
  @property({ type: String, reflect: true, state: false })
  close: "outside" | "default" | "inside" = "default";

  @queryAssignedElements({ slot: "toggler", flatten: true })
  private _toggler: Array<HTMLElement>;

  protected menuRef;

  private async _handleClick(e: MouseEvent) {
    if (this.disabled) return;

    const slottedToggler = this._toggler[0];

    if (!slottedToggler) {
      this.toggleMenu();
      return;
    }

    if (e.composedPath().includes(slottedToggler)) {
      this.toggleMenu();
      return;
    }

    // Click landed in the toggler-container's empty area (passed through pointer-events: none)
    if (!this.menuIsOpen) return;
    const menu = this.menuRef.value;
    if (menu && e.composedPath().includes(menu)) return;
    this.hideMenu();
  }

  private _handleCloseMenu() {
    const button = this._toggler[0];
    button?.focus();
  }

  async connectedCallback() {
    super.connectedCallback();
    this.addEventListener("sgds-hide", this._handleCloseMenu);
  }

  async disconnectedCallback() {
    this.removeEventListener("sgds-hide", this._handleCloseMenu);
  }

  async firstUpdated(changedProperties: PropertyValueMap<this>) {
    super.firstUpdated(changedProperties);
    if (this.menuIsOpen) {
      await this.updateFloatingPosition();
    }
    this._handleDisabled();
  }

  private _handleTogglerSlotChange(e: Event) {
    const slot = e.target as HTMLSlotElement;
    const elements = slot.assignedElements({ flatten: true });
    const button = elements[0];
    if (button) {
      button.setAttribute("aria-haspopup", "menu");
      button.setAttribute("aria-expanded", String(this.menuIsOpen));
    }
  }

  @watch("menuIsOpen")
  _handleMenuIsOpenChange() {
    const button = this._toggler[0];
    if (button) {
      button.setAttribute("aria-expanded", String(this.menuIsOpen));
    }
  }

  @watch("disabled", { waitUntilFirstUpdate: true })
  _handleDisabled() {
    const button = this._toggler[0];
    if (button) {
      if (this.disabled) {
        button.setAttribute("disabled", "true");
      } else {
        button.hasAttribute("disabled") && button.removeAttribute("disabled");
      }
    }
  }

  render() {
    return html`
      <div class="dropdown" @click=${this._handleClick}>
        <div class="toggler-container" ${ref(this.myDropdown)}>
          <slot name="toggler" @slotchange=${this._handleTogglerSlotChange}></slot>
        </div>
        <div class="dropdown-menu" role="menu" ${ref(this.menuRef)}>
          <slot id="default" @click=${this.handleSelectSlot}></slot>
        </div>
      </div>
    `;
  }
}

export default SgdsDropdown;
